// INSIDO (18-feedback-teste): image uploads to autumn that cannot fail silently.
//
// Upstream `client.uploadFile` never checks the HTTP status: a rejected upload
// (autumn answers 422 `FileTooLarge`, max 4 MB for avatars/icons) parses as
// `{ type: "FileTooLarge" }`, returns `undefined` as the file id and the
// profile is then saved without the new picture — which is what the owner saw
// as "the avatar upload is not happening". This helper:
//   1. downscales raster images on the client (canvas) so a phone photo always
//      fits: avatars/icons to 512 px, banners/backgrounds to 1920 px, WebP;
//      animated GIFs and SVGs are sent untouched;
//   2. POSTs itself and throws an `UploadError` (with autumn's `type` and
//      `max`) on any non-2xx answer, so callers can show a message.
import type { Client } from "stoat.js";

/** Size presets per autumn tag */
export const IMAGE_LIMITS: Record<string, { maxSide: number }> = {
  avatars: { maxSide: 512 },
  icons: { maxSide: 512 },
  banners: { maxSide: 1920 },
  backgrounds: { maxSide: 1920 },
};

/** Autumn's upload limit for these tags (bytes) */
export const AUTUMN_MAX_BYTES = 4_000_000;

/** Error thrown when autumn rejects an upload */
export class UploadError extends Error {
  constructor(
    /** autumn error type, e.g. "FileTooLarge" */
    public readonly type: string,
    /** HTTP status */
    public readonly status: number,
    /** `max` reported by autumn for FileTooLarge (bytes) */
    public readonly max?: number,
  ) {
    super(`upload failed: ${type} (${status})`);
  }
}

/**
 * Whether a file should be downscaled before upload
 * @param file Picked file
 */
export function shouldDownscale(file: Pick<File, "type">): boolean {
  if (!file.type.startsWith("image/")) return false;
  // keep animation / vector data as is
  return file.type !== "image/gif" && file.type !== "image/svg+xml";
}

/**
 * Side of the downscaled image for a tag
 * @param tag autumn tag
 */
export function maxSideFor(tag: string): number {
  return IMAGE_LIMITS[tag]?.maxSide ?? 2048;
}

/**
 * Downscale a raster image so its longest side is at most `maxSide`,
 * re-encoding as WebP. Returns the original file when nothing is needed
 * (already small enough and under the byte limit) or when decoding fails.
 * @param file Picked file
 * @param maxSide Longest side in px
 */
export async function downscaleImage(
  file: File,
  maxSide: number,
): Promise<File> {
  if (!shouldDownscale(file)) return file;
  if (typeof createImageBitmap !== "function") return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }

  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  if (scale === 1 && file.size <= AUTUMN_MAX_BYTES) {
    bitmap.close();
    return file;
  }

  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/webp", 0.9),
  );
  if (!blob) return file;

  const name = file.name.replace(/\.[a-z0-9]+$/i, "") + ".webp";
  return new File([blob], name, { type: "image/webp" });
}

/**
 * Upload an image to autumn, downscaled for its tag, failing loudly.
 * @param client Stoat client (session header)
 * @param tag autumn tag ("avatars", "icons", "banners", "backgrounds")
 * @param file Picked file
 * @param uploadUrl autumn base URL (defaults to the configured one)
 * @returns autumn file id
 */
export async function uploadImage(
  client: Client,
  tag: string,
  file: File,
  uploadUrl?: string,
): Promise<string> {
  const prepared = await downscaleImage(file, maxSideFor(tag));

  const body = new FormData();
  body.append("file", prepared);
  const [key, value] = client.authenticationHeader;

  const res = await fetch(
    `${uploadUrl ?? client.configuration?.features.autumn.url}/${tag}`,
    { method: "POST", body, headers: { [key]: value } },
  );

  let data: { id?: string; type?: string; max?: number } = {};
  try {
    data = await res.json();
  } catch {
    // non-JSON body (proxy error page): fall through to the status check
  }

  if (!res.ok || !data.id) {
    throw new UploadError(data.type ?? "Unknown", res.status, data.max);
  }

  return data.id;
}

/**
 * pt-PT message for an upload failure (used by the editors' snackbars)
 * @param error Thrown error
 * @param messages Translated strings from the caller
 */
export function describeUploadError(
  error: unknown,
  messages: { tooLarge: (maxMb: number) => string; generic: string },
): string {
  if (error instanceof UploadError && error.type === "FileTooLarge") {
    return messages.tooLarge(
      Math.round(((error.max ?? AUTUMN_MAX_BYTES) / 1_000_000) * 10) / 10,
    );
  }
  return messages.generic;
}
